/**
 * Zod contracts for the provenance the bar spirit-list generator stamps on the
 * modifier groups and modifiers it writes (design 2026-09-27 §6.3). Enums are
 * derived from the const tuples in `@order-up/models`, so a new list key is
 * accepted by every layer at once or by none.
 */

import {
  BAR_BASE_POUR_OZ_DEFAULT,
  BAR_DOUBLE_MULTIPLIER_MAX,
  BAR_LIST_KEYS,
  BAR_MIXER_DISPENSES,
  BAR_OPTION_PRICE_MAX,
  BAR_POUR_OZ_MAX,
  BAR_RETIRED_STYLE_KEYS,
  BAR_SPIRIT_TYPES,
  BAR_STYLE_KEYS,
  BAR_STYLE_INSTRUCTION_KEYS,
  BAR_STYLE_INSTRUCTIONS_MAX,
  barStylePourCapProblems,
  isValidBarDoubleMultiplier,
  isValidBarOptionPrice,
  isValidBarPourOz,
  type BarStyleKey,
} from "@order-up/models";
import { z } from "zod";

export const BarListKeySchema = z.enum(BAR_LIST_KEYS);
export const BarStyleKeySchema = z.enum(BAR_STYLE_KEYS);
/** A style, or Double — what a style instruction is written for. */
export const BarStyleInstructionKeySchema = z.enum(BAR_STYLE_INSTRUCTION_KEYS);
/** A location's own how-to for a style: trimmed, 1..`BAR_STYLE_INSTRUCTIONS_MAX` characters. */
export const BarStyleInstructionSchema = z
  .string()
  .trim()
  .min(1, { message: "Write the instructions, or reset to the OrderUp standard." })
  .max(BAR_STYLE_INSTRUCTIONS_MAX, { message: `Keep the instructions to ${BAR_STYLE_INSTRUCTIONS_MAX} characters.` });
/**
 * A stored option's `styleKey`: a current style, or a retired one
 * (`BAR_RETIRED_STYLE_KEYS` — Double was a style before bar v2) on an option
 * archived since. Read-side only: nothing generates a retired style again.
 */
const BarStoredStyleKeySchema = z.enum([...BAR_STYLE_KEYS, ...BAR_RETIRED_STYLE_KEYS]);

/** A pour in ounces: more than 0, at most `BAR_POUR_OZ_MAX`, two decimals. */
export const BarPourOzSchema = z.number().refine(isValidBarPourOz, {
  message: `A pour is more than 0 and at most ${BAR_POUR_OZ_MAX} oz, to two decimals.`,
});

/** What Double pours relative to the style picked: more than 1, at most `BAR_DOUBLE_MULTIPLIER_MAX`. */
export const BarDoubleMultiplierSchema = z.number().refine(isValidBarDoubleMultiplier, {
  message: `Double pours more than 1× and at most ${BAR_DOUBLE_MULTIPLIER_MAX}× the style.`,
});

/** A settings-entered bar price: 0 to `BAR_OPTION_PRICE_MAX`, whole cents (`isValidBarOptionPrice`). */
export const BarSettingsPriceSchema = z.number().refine(isValidBarOptionPrice, {
  message: `A price is 0 to ${BAR_OPTION_PRICE_MAX}, in whole cents.`,
});

/** `IModifierGroup.barList` — present only on a generated group. */
export const BarListGroupProvenanceSchema = z
  .object({
    key: BarListKeySchema,
    generatedAt: z.date(),
    required: z.boolean().optional(),
    order: z.array(z.number().int()).optional(),
    orderedByRestaurant: z.boolean().optional(),
    posPromptSeededAt: z.date().optional(),
  })
  .strict();

/** `IModifier.barOption` — present only on a generated option. */
export const BarListOptionProvenanceSchema = z
  .object({
    listKey: BarListKeySchema,
    inventoryGuid: z.string().min(1).optional(),
    styleKey: BarStoredStyleKeySchema.optional(),
    drawBasis: z.string().optional(),
    writtenPrice: z.number().min(0).optional(),
    heldUnpriced: z.boolean().optional(),
    sortRank: z.number().optional(),
    sortName: z.string().optional(),
    /** A mixer choice on the Style group: its kind (2026-10-03). */
    mixerKind: z.enum(BAR_MIXER_DISPENSES).optional(),
    /** A mixer choice's upcharge over the Mixed price; absent = none. */
    upcharge: BarSettingsPriceSchema.optional(),
    /**
     * The till / chit names and the photo OrderUp last wrote onto this option
     * from its row (design 2026-10-05 Parts 1–2) — only a value equal to one
     * of these is removed when the row is cleared. Listed because this schema
     * is `.strict()`: without them every option CREATED for a named or
     * photographed row failed validation.
     */
    writtenPosName: z.string().optional(),
    writtenKitchenName: z.string().optional(),
    writtenImageUrl: z.string().url().optional(),
  })
  .strict();

const BarSpiritSettingsPriceSchema = z.number().min(0).max(BAR_OPTION_PRICE_MAX);

/**
 * `restaurant.settings.barSpiritLists` as STORED — the sparse settings block
 * (`RestaurantBarSpiritListsBlock`). No defaults and no nulls: an absent key IS
 * the default, and a cleared price is deleted, never stored as `null`.
 * SERVER-WRITTEN only (`BarSpiritListService.saveSettings`).
 *
 * The retired keys (`LOCATION_BAR_SPIRIT_LISTS_RETIRED_KEYS` — `tierPrices`,
 * `mixerPrice`, `required`, `standardPourOz`) and the retired Double STYLE
 * price (`stylePrices.double`) are refused: a save never writes one back.
 * (`BarSpiritListService` re-attaches a STORED retired Double style price
 * after this parse, until a `doublePrice` is saved — it is never written new.)
 *
 * Block-level: no style may pour more than the order-close cap
 * (`BAR_POUR_MULTIPLIER_MAX`) times the base pour — an absent base is the
 * default — or regeneration would write a multiplier order close refuses.
 */
export const LocationBarSpiritListsBlockSchema = z
  .object({
    enabled: z.array(BarListKeySchema).max(32).readonly().optional(),
    stylePrices: z.partialRecord(BarStyleKeySchema, BarSpiritSettingsPriceSchema).readonly().optional(),
    doublePrice: BarSettingsPriceSchema.optional(),
    stylePours: z.partialRecord(BarStyleKeySchema, BarPourOzSchema).readonly().optional(),
    basePourOz: BarPourOzSchema.optional(),
    doubleMultiplier: BarDoubleMultiplierSchema.optional(),
    /** Each spirit type's house ROLE (`IBarSpiritSettings.houseRoles`) — written only by `setHousePour`. */
    houseRoles: z.partialRecord(z.enum(BAR_SPIRIT_TYPES), z.string().min(1).max(128)).readonly().optional(),
    /** The location's own how-to per style / Double (`IBarSpiritSettings.styleInstructions`); absent = the standard. */
    styleInstructions: z.partialRecord(BarStyleInstructionKeySchema, BarStyleInstructionSchema).readonly().optional(),
    /** The mixer kinds offered as Style choices (`IBarSpiritSettings.mixerKinds`); only `true` is stored. */
    mixerKinds: z.partialRecord(z.enum(BAR_MIXER_DISPENSES), z.literal(true)).readonly().optional(),
  })
  .strict()
  .superRefine((block, ctx) => {
    const base = isValidBarPourOz(block.basePourOz) ? block.basePourOz : BAR_BASE_POUR_OZ_DEFAULT;
    const stylePours: Partial<Record<BarStyleKey, number>> = {};
    for (const [k, v] of Object.entries(block.stylePours ?? {})) {
      if (isValidBarPourOz(v)) stylePours[k as BarStyleKey] = v;
    }
    for (const p of barStylePourCapProblems({ basePourOz: base, stylePours })) {
      ctx.addIssue({ code: "custom", message: p.message, path: ["stylePours", p.style] });
    }
  });
